import type { ReactNode } from "react";

import type { RelationshipStageEvent } from "../../lib/relationship-api";
import {
  RELATIONSHIP_STAGE_LABELS,
  RELATIONSHIP_STAGE_ORDER,
  isRelationshipStage,
  stageNodeStatus,
} from "../../lib/relationship-stage";
import { cn } from "../../lib/utils";

/**
 * RelationshipStageTrack (2026-10-09) — the ONE rendering of the
 * connected → agreement → active → under contract → closed spine. Shared by
 * the relationship profile page (`/team/:proUserId`) and the connected Home
 * stack, so the two can never disagree about where a buyer stands.
 *
 * A segmented progress track (five rounded segments + labels) under a
 * current-stage headline, replacing the circle stepper. Every value comes
 * from the raw wire enum (`relationship_stage`) via `lib/relationship-stage`
 * — no copy is invented here. Per-stage dates come only from real
 * `stage_events` (and `connectedAt` for the first stage); a stage with no
 * dated event shows no date rather than a guessed one. An unknown stage
 * fails closed: no headline, every segment "upcoming".
 *
 * `data-node` / `data-status` on each `<li>` are the contract the page tests
 * (and any Playwright walk) read.
 */
export interface RelationshipStageTrackProps {
  currentStage: string | null | undefined;
  events?: readonly RelationshipStageEvent[];
  /** When the connection was made — dates the "Connected" segment. */
  connectedAt?: string;
  /** Small caps label above the headline. */
  eyebrow?: string;
  /** Right-aligned header slot (a link, a badge). */
  action?: ReactNode;
  listTestId?: string;
  className?: string;
}

function shortDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Latest event that ENTERED each stage — a revert then re-advance dates the
 *  stage by its most recent entry. */
function stageDates(
  events: readonly RelationshipStageEvent[],
  connectedAt: string | undefined,
): Partial<Record<string, string>> {
  const out: Partial<Record<string, string>> = {};
  if (connectedAt) out.connected = connectedAt;
  for (const event of events) {
    const prior = out[event.to_stage];
    if (!prior || new Date(event.created_at) > new Date(prior)) {
      out[event.to_stage] = event.created_at;
    }
  }
  return out;
}

export function RelationshipStageTrack({
  currentStage,
  events = [],
  connectedAt,
  eyebrow,
  action,
  listTestId = "relationship-stage-nodes",
  className,
}: RelationshipStageTrackProps) {
  const known = currentStage && isRelationshipStage(currentStage) ? currentStage : null;
  const index = known ? RELATIONSHIP_STAGE_ORDER.indexOf(known) : -1;
  const next = index >= 0 ? RELATIONSHIP_STAGE_ORDER[index + 1] : undefined;
  const dates = stageDates(events, connectedAt);
  const total = RELATIONSHIP_STAGE_ORDER.length;

  return (
    <div className={cn("space-y-4", className)} data-testid="relationship-stage-track">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          {eyebrow ? <p className="eyebrow-label">{eyebrow}</p> : null}
          {known ? (
            <>
              <p className="text-xl font-bold leading-tight text-foreground" data-testid="relationship-stage-current">
                {RELATIONSHIP_STAGE_LABELS[known]}
              </p>
              <p className="text-sm text-foreground/70">
                Stage {index + 1} of {total}
                {next ? ` · Next: ${RELATIONSHIP_STAGE_LABELS[next]}` : ""}
              </p>
            </>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>

      <ol
        aria-label="Relationship stages"
        className="grid grid-cols-5 gap-1.5 sm:gap-2"
        data-testid={listTestId}
      >
        {RELATIONSHIP_STAGE_ORDER.map((stage) => {
          const status = stageNodeStatus(currentStage, stage);
          const date = status === "upcoming" ? null : shortDate(dates[stage]);
          return (
            <li
              aria-current={status === "now" ? "step" : undefined}
              className="min-w-0 space-y-2"
              data-node={stage}
              data-status={status}
              key={stage}
            >
              <span
                aria-hidden
                className={cn(
                  "block h-2 rounded-full",
                  status === "done" && "bg-primary/45",
                  status === "now" && "bg-primary ring-4 ring-primary/15",
                  status === "upcoming" && "bg-muted",
                )}
              />
              <span
                className={cn(
                  "block text-xs leading-snug",
                  status === "now" && "font-semibold text-foreground",
                  status === "done" && "text-foreground/70",
                  status === "upcoming" && "text-muted-foreground",
                  // On a phone the five labels can't all fit; the headline
                  // already names the current stage.
                  "max-sm:sr-only",
                )}
              >
                {RELATIONSHIP_STAGE_LABELS[stage]}
              </span>
              {date ? (
                <span className="block text-[11px] tabular-nums text-muted-foreground max-sm:hidden">
                  {date}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
